<!--
  车站面板：查询本站班次 → 上车 → 沿途报站过场 → 到站落地场景。
  数据来源：/map/coach/routes 与 /map/coach/ride（后端 coach 业务）。
  报站序列由后端按八方向连通图跑 BFS 得出，前端只负责按节奏点亮，不维护第二份站序配置。
-->
<template>
  <div>
    <el-dialog :visible.sync="visible" :before-close="close" append-to-body
      :title="dialogTitle" width="520px" :close-on-click-modal="false" custom-class="coach-dialog">
      <div v-loading="loading" class="coach-body">
        <div class="coach-head">
          <span class="coach-station">当前站点 · {{ fromName || '—' }}</span>
          <span class="coach-money">余额 {{ moneyText }} 元</span>
        </div>

        <div v-if="!loading && routes.length === 0" class="coach-empty">
          这个站台暂时没有班车。
        </div>

        <div v-for="r in routes" :key="r.toId" class="coach-route">
          <div class="coach-route-main">
            <div class="coach-route-name">{{ r.routeName }} · 开往 {{ r.toName }}</div>
            <div class="coach-route-meta">
              <span class="meta-price">票价 {{ priceText(r.price) }} 元</span>
              <span class="meta-dot">·</span>
              <span>{{ r.minLevel > 0 ? ('需 ' + r.minLevel + ' 级') : '不限等级' }}</span>
            </div>
          </div>
          <el-button type="primary" size="small" :disabled="!canRide(r)"
            @click="doRide(r)">{{ rideBtnText(r) }}</el-button>
        </div>

        <div class="coach-tip">上车前请确认随身物品；途中会依次经过沿线各站。</div>
      </div>
    </el-dialog>

    <!-- 乘车过场：盖在弹窗之上，逐站报站。刻意不提供跳过 —— 过场期间后端已完成位移与扣费 -->
    <coach-passage v-if="riding" :stations="stations" :route-line="passageLine"
      @finish="finishPassage" />
  </div>
</template>

<script>
import {postRequest} from '../../utils/api'
import coachPassage from './coachPassage'

export default {
  name: 'coachPanel',
  components: {coachPassage},
  props: {
    coachVisible: {
      type: Boolean,
      default: false
    },
    // 来自 special_event.event_param，用于校验「这个按钮对应哪条线」
    routeKey: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      visible: false,
      fromMapId: '',
      fromName: '',
      money: 0,
      routes: [],
      // 过场状态：逐站播放交给 coachPassage，这里只保留「有没有在坐车」与待落地的场景
      riding: false,
      stations: [],
      passageLine: '',
      pendingScene: null
    }
  },
  computed: {
    dialogTitle() {
      return this.fromName ? ('车站 · ' + this.fromName) : '车站 · 乘车'
    },
    moneyText() {
      const n = Number(this.money || 0)
      return n.toFixed(0)
    }
  },
  watch: {
    // immediate：父组件用 v-if 包着本组件，挂载瞬间 coachVisible 就已经是 true，
    // 普通 watch 不会触发，面板会停在空列表上。
    coachVisible: {
      immediate: true,
      handler(val) {
        this.visible = val
        if (val) {
          this.loadRoutes()
        }
      }
    }
  },
  methods: {
    priceText(p) {
      const n = Number(p || 0)
      return n % 1 === 0 ? String(n) : n.toFixed(2)
    },
    canRide(r) {
      return !!r.affordable && !!r.levelOk
    },
    rideBtnText(r) {
      if (!r.levelOk) return '需 ' + r.minLevel + ' 级'
      if (!r.affordable) {
        const gap = Number(r.price || 0) - Number(this.money || 0)
        return '还差 ' + this.priceText(gap) + ' 元'
      }
      return '上车'
    },
    loadRoutes() {
      this.loading = true
      this.routes = []
      postRequest('/map/coach/routes', {}).then(resp => {
        const body = resp && resp.data
        if (resp && resp.status == 200 && body && body.status == 200 && body.data) {
          const d = body.data
          this.fromMapId = d.fromMapId || ''
          this.fromName = d.fromName || ''
          this.money = d.money || 0
          this.routes = d.routes || []
        } else {
          this.$message.error((body && body.message) || '车站信息读取失败')
        }
      }).catch(() => {
        this.$message.error('网络异常，请稍后重试')
      }).finally(() => {
        this.loading = false
      })
    },
    doRide(route) {
      if (this.riding) return
      this.$confirm(
        '乘坐' + route.routeName + '前往' + route.toName + '，票价 ' + this.priceText(route.price) + ' 元。',
        '确认乘车',
        {confirmButtonText: '上车', cancelButtonText: '再想想', type: 'warning'}
      ).then(() => {
        this.ride(route)
      }).catch(() => {})
    },
    ride(route) {
      this.loading = true
      postRequest('/map/coach/ride', {
        toId: String(route.toId),
        routeKey: this.routeKey || route.routeKey
      }).then(resp => {
        const body = resp && resp.data
        if (resp && resp.status == 200 && body && body.status == 200 && body.data) {
          // 请求期间玩家已经关掉面板：不要再起过场，否则组件销毁后定时器还在跑
          if (!this.visible) {
            return
          }
          const data = body.data
          this.pendingScene = data
          this.stations = (data.stations && data.stations.length > 0)
            ? data.stations
            : [{id: data.fromId, name: this.fromName}, {id: data.targetId, name: route.toName}]
          this.passageLine = (data.routeName || route.routeName) + ' · 开往 ' + (data.coachToName || route.toName)
          this.startPassage()
        } else {
          this.$message.error((body && body.message) || '乘车失败')
        }
      }).catch(() => {
        this.$message.error('网络异常，请稍后重试')
      }).finally(() => {
        this.loading = false
      })
    },
    startPassage() {
      this.riding = true
    },
    finishPassage() {
      // 已经落地过又收到一次（关窗与组件 finish 同时触发）：直接返回，不要重复结算
      if (!this.pendingScene) {
        this.riding = false
        return
      }
      this.riding = false
      const scene = this.pendingScene
      this.pendingScene = null
      // 场景落地放在过场结束时：过场期间玩家看到的是「车在开」，此刻才该换成到达站
      this.$store.dispatch('street/applyRideScene', scene)
      this.$emit('coach_dialog_close')
      this.visible = false
    },
    close(done) {
      // 过场途中直接关弹窗（点 X / 按 ESC）：车已经开了，后端早已位移并扣费。
      // 这里必须按「到站」处理，否则场景永远不落地 —— 人站在新站、
      // 界面还停在旧街，下一次移动就会被判「不能往那边走」。
      if (this.riding) {
        this.finishPassage()
      } else {
        this.visible = false
        this.$emit('coach_dialog_close')
      }
      // el-dialog 的 before-close 只有调了 done 才会真正隐藏，不调会卡在「关不掉」的状态
      if (typeof done === 'function') {
        done()
      }
    }
  }
}
</script>

<style scoped>
.coach-body { min-height: 120px; }
.coach-head {
  display: flex; justify-content: space-between; align-items: baseline;
  font-size: 13px; color: #6b7684; padding-bottom: 10px; border-bottom: 1px solid #eceff2;
}
.coach-station { color: #2b3138; font-weight: 600; }
.coach-empty { padding: 28px 0; text-align: center; color: #9aa5b1; font-size: 13px; }
.coach-route {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 12px; margin-top: 10px;
  background: #f7f9fb; border: 1px solid #e6ebf0; border-radius: 8px;
}
.coach-route-name { font-size: 14px; font-weight: 600; color: #1f2429; }
.coach-route-meta { margin-top: 5px; font-size: 12px; color: #78838f; }
.coach-route-meta .meta-price { color: #b3382f; font-weight: 600; }
.coach-route-meta .meta-dot { margin: 0 5px; color: #c8d0d8; }
.coach-tip { margin-top: 14px; font-size: 12px; color: #9aa5b1; }
</style>
